import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Stack,
  Divider,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle,
  Assessment,
  Build,
  Warning,
  Speed,
  Person,
  Category,
  Description,
  Timeline
} from '@mui/icons-material';

interface PCAData {
  customerName?: string;
  issueCategory?: string;
  issueDetails?: string;
  troubleshootingSteps?: string;
  resolutionStatus?: string;
  urgencyLevel?: string;
  aiConfidence?: number;
  callId?: string;
  duration?: number;
}

interface PCADashboardProps {
  pcaData: PCAData;
  callMetadata?: {
    startTime?: string;
    endTime?: string;
    duration?: number;
  };
}

const PCADashboard: React.FC<PCADashboardProps> = ({ pcaData, callMetadata }) => {
  const getUrgencyColor = (urgency?: string) => {
    if (!urgency) return 'default';
    const urg = urgency.toLowerCase();
    if (urg === 'critical') return 'error';
    if (urg === 'high') return 'warning';
    if (urg === 'medium') return 'info';
    return 'success';
  };

  const getResolutionColor = (status?: string) => {
    if (!status) return 'default';
    const stat = status.toLowerCase();
    if (stat === 'resolved') return 'success';
    if (stat === 'critical' || stat === 'high') return 'error';
    if (stat === 'medium') return 'warning';
    return 'info';
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatSteps = (steps?: string): string[] => {
    if (!steps) return ['No troubleshooting steps recorded'];
    
    // Try to split by numbered list first (e.g., "1.", "2.", etc.)
    const numberedSplit = steps.split(/(?=\d+\.)/);
    if (numberedSplit.length > 1) {
      return numberedSplit
        .map(s => s.trim().replace(/^\d+\.\s*/, ''))
        .filter(s => s.length > 0);
    }
    
    // Try splitting by newlines
    const lineSplit = steps.split(/\n+/).filter(s => s.trim().length > 0);
    if (lineSplit.length > 1) {
      return lineSplit.map(s => s.trim().replace(/^[•-]\s*/, ''));
    }
    
    // If no clear delimiters, return as single step
    return [steps.trim()];
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#080707', mb: 3 }}>
        Post-Call Analysis
      </Typography>

      {/* KPI Cards Row */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {/* Resolution Status */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            border: '1px solid #E0E0E0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Box sx={{ 
                  bgcolor: getResolutionColor(pcaData.resolutionStatus) === 'success' ? '#4CAF50' : '#FF9800',
                  borderRadius: 1.5,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48
                }}>
                  {pcaData.resolutionStatus?.toLowerCase() === 'resolved' ? (
                    <CheckCircle sx={{ color: 'white', fontSize: 28 }} />
                  ) : (
                    <Assessment sx={{ color: 'white', fontSize: 28 }} />
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#706E6B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                    Resolution Status
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#080707', mt: 0.5 }}>
                    {pcaData.resolutionStatus || 'Pending'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>


        {/* Urgency Level */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            border: '1px solid #E0E0E0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Box sx={{ 
                  bgcolor: '#FF9800',
                  borderRadius: 1.5,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48
                }}>
                  <Warning sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#706E6B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                    Urgency Level
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={pcaData.urgencyLevel || 'Low'}
                      color={getUrgencyColor(pcaData.urgencyLevel)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* AI Confidence */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            border: '1px solid #E0E0E0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            }
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Box sx={{ 
                  bgcolor: '#00BCD4',
                  borderRadius: 1.5,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48
                }}>
                  <Speed sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#706E6B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                    AI Confidence
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={pcaData.aiConfidence ? (pcaData.aiConfidence * 100) : 0} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        backgroundColor: '#E0E0E0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: pcaData.aiConfidence && pcaData.aiConfidence > 0.8 ? '#4CAF50' : '#2196F3',
                          borderRadius: 1
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 700, color: '#080707' }}>
                      {pcaData.aiConfidence ? `${Math.round(pcaData.aiConfidence * 100)}%` : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Issue Details */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #DDDBDA', height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Description color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#080707' }}>
                  Issue Details
                </Typography>
              </Stack>
              <Paper sx={{ p: 2, backgroundColor: '#F9F9F9', border: '1px solid #DDDBDA' }}>
                <Typography variant="body2" sx={{ color: '#080707', lineHeight: 1.6 }}>
                  {pcaData.issueDetails || 'No details available'}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Troubleshooting Steps */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #DDDBDA', height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Build color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#080707' }}>
                  Troubleshooting Steps
                </Typography>
              </Stack>
              <Paper 
                sx={{ 
                  p: 3, 
                  backgroundColor: '#FAFAFA', 
                  border: '1px solid #DDDBDA',
                  maxHeight: '400px',
                  overflow: 'auto'
                }}
              >
                {formatSteps(pcaData.troubleshootingSteps).map((step, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: 2.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: '#0176D3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 2,
                        flexShrink: 0,
                        boxShadow: '0 2px 4px rgba(1, 118, 211, 0.2)'
                      }}
                    >
                      <Typography 
                        sx={{ 
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.875rem'
                        }}
                      >
                        {index + 1}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#080707', 
                        lineHeight: 1.7,
                        fontSize: '0.9rem',
                        flex: 1,
                        pt: 0.5
                      }}
                    >
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Bottom Row - Customer Info & Call Metadata */}
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid #DDDBDA' }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                    <Person color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#080707' }}>
                      Customer Information
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Customer Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#080707' }}>
                        {pcaData.customerName || 'Unknown'}
                      </Typography>
                    </Box>
                    {pcaData.callId && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Call ID
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#706E6B', fontFamily: 'monospace' }}>
                          {pcaData.callId}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                    <Timeline color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#080707' }}>
                      Call Metadata
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#080707' }}>
                        {formatDuration(pcaData.duration || callMetadata?.duration)}
                      </Typography>
                    </Box>
                    {callMetadata?.startTime && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Start Time
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#706E6B' }}>
                          {new Date(callMetadata.startTime).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PCADashboard;

